/**
 * A tiny, safe markdown renderer for JARVIS replies: paragraphs, "-" / "1."
 * lists, **bold**, *italic*, `code`, and [label](url) links. Builds React
 * elements only — never dangerouslySetInnerHTML — so model or database text
 * can't inject markup. Links: in-app paths ("/contractors/12") route through
 * onNavigate; http(s) opens in a new tab; any other scheme renders as text.
 */
import React from 'react';

const INLINE = /(\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)|\*([^*\s][^*]*)\*)/g;

function renderInline(text, onNavigate, keyBase) {
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const k = `${keyBase}-${i++}`;
    if (m[2]) out.push(<strong key={k}>{m[2]}</strong>);
    else if (m[3]) out.push(<code key={k}>{m[3]}</code>);
    else if (m[4]) {
      const href = m[5];
      if (/^\/[A-Za-z0-9/_\-?=&%.]*$/.test(href)) {
        out.push(
          <a key={k} href={href} className="jv-link"
            onClick={(e) => { if (onNavigate && !e.metaKey && !e.ctrlKey) { e.preventDefault(); onNavigate(href); } }}>
            {m[4]}
          </a>,
        );
      } else if (/^https?:\/\//i.test(href)) {
        out.push(<a key={k} href={href} className="jv-link" target="_blank" rel="noopener noreferrer">{m[4]}</a>);
      } else {
        out.push(m[4]);
      }
    } else if (m[6]) out.push(<em key={k}>{m[6]}</em>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function SafeMarkdown({ text, onNavigate }) {
  const lines = String(text || '').replace(/\r/g, '').split('\n');
  const blocks = [];
  let list = null;
  let para = [];
  const flushPara = () => {
    if (para.length) { blocks.push({ type: 'p', text: para.join(' ') }); para = []; }
  };
  const flushList = () => { if (list) { blocks.push(list); list = null; } };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    const heading = /^\s{0,3}#{1,6}\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      flushPara();
      const type = bullet ? 'ul' : 'ol';
      if (!list || list.type !== type) { flushList(); list = { type, items: [] }; }
      list.items.push((bullet || numbered)[1]);
    } else if (heading) {
      flushPara(); flushList();
      blocks.push({ type: 'h', text: heading[1] });
    } else if (!line.trim()) {
      flushPara(); flushList();
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara(); flushList();

  return (
    <div className="jv-md">
      {blocks.map((b, i) => {
        if (b.type === 'p') return <p key={i}>{renderInline(b.text, onNavigate, `p${i}`)}</p>;
        if (b.type === 'h') return <p key={i} className="jv-md-h">{renderInline(b.text, onNavigate, `h${i}`)}</p>;
        const Tag = b.type;
        return (
          <Tag key={i}>
            {b.items.map((it, j) => <li key={j}>{renderInline(it, onNavigate, `l${i}-${j}`)}</li>)}
          </Tag>
        );
      })}
    </div>
  );
}
